import { forwardRef, IconButton as ChakraIconButton, IconButtonProps as ChakraIconButtonProps } from '@chakra-ui/react';
import Icon, { IconProps, TypeIconName } from '../Icon/Icon';
import Tooltip, { TooltipProps } from '../Tooltip/Tooltip';

export interface IconButtonProps extends ChakraIconButtonProps {
  as?: 'a' | 'button' | 'span';
  disabled?: never;
  iconName: TypeIconName;
  isDanger?: boolean;
  iconSize?: IconProps['size'];
  isTooltipDisabled?: boolean;
  label?: string;
  size?: 'sm' | 'md' | 'lg';
  tooltipCloseDelay?: number;
  tooltipProps?: Omit<TooltipProps, 'children'>;
  variant?: 'primary' | 'secondary' | 'tertiary' | 'danger-primary' | 'danger-secondary' | 'danger-tertiary';
}

/**
 * IconButton composes the `Button` component except that it renders only an icon.
 */
const IconButton = forwardRef<IconButtonProps, 'button'>((props, ref) => {
  const {
    as = 'button',
    iconName,
    iconSize,
    isDanger,
    isDisabled,
    isTooltipDisabled,
    label,
    size = 'lg',
    tooltipCloseDelay = 0,
    tooltipProps,
    variant = 'primary',
    ...rest
  } = props;
  const properties: ChakraIconButtonProps = {
    as: as === 'a' && isDisabled ? 'button' : as,
    icon: <Icon name={iconName} size={iconSize || (size === 'lg' ? '24' : '16')} />,
    isDisabled,
    size,
    variant: isDanger ? `danger-${variant}` : variant,
    ...rest,
  };
  return (
    <Tooltip
      closeDelay={tooltipCloseDelay}
      isDisabled={isTooltipDisabled}
      label={label || rest['aria-label']}
      shouldWrapChildren={isDisabled}
      {...tooltipProps}
    >
      <ChakraIconButton {...properties} ref={ref} />
    </Tooltip>
  );
});

export default IconButton;
